import axiosNew from '../../helpers/axios'
import { formatStoryMapMessage } from './storyMap.service'

/**
 * Storymap Agent API layer — a single call: post the transcript files as
 * multipart form-data, get the drafted story map JSON back. No S3, no
 * connection state. Same never-throw convention as storyMap.service.js:
 * resolves `{ success, result, message }` so callers only branch on `success`.
 */

export const STORY_MAP_AGENT_TRANSCRIPT_ACCEPT = '.txt,.md,.vtt,.docx,.pdf'
export const STORY_MAP_AGENT_MAX_FILES = 10
export const STORY_MAP_AGENT_MAX_TOTAL_BYTES = 50 * 1024 * 1024

export const isAllowedAgentTranscript = (file) => {
    const name = String(file?.name ?? '').toLowerCase()
    return STORY_MAP_AGENT_TRANSCRIPT_ACCEPT.split(',').some((ext) =>
        name.endsWith(ext.trim())
    )
}

// Generation is one long synchronous LLM pipeline — mirror the backend's own
// AI-call budget instead of axios's default (no) timeout so a hung request
// eventually surfaces as an error the modal can show.
const GENERATE_TIMEOUT_MS = 15 * 60 * 1000

/**
 * Upload the transcript files and run generation in one STREAMING request.
 *
 * The server responds with NDJSON lines while the pipeline runs:
 *   {"kind":"status","message":...}   — stage progress
 *   {"kind":"journey",...}            — one generated journey + its stories
 *                                       (update mode only; render immediately)
 *   {"kind":"result","data":...}      — terminal: the full AI payload
 *   {"kind":"error",...}              — terminal: the run failed
 *
 * Every parsed event is passed to `onEvent`. The returned promise resolves
 * from the terminal event: { success, result, message } where result is the
 * same { story_map: { board }, changes?, ... } payload as before.
 *
 * `onUploadProgress` (optional) receives a 0..1 fraction while the files are
 * being sent — after it reaches 1 the server is generating.
 *
 * Implementation note: axios in the browser uses XHR, so the stream is read
 * incrementally from xhr.responseText inside onDownloadProgress — each call
 * parses only the complete lines added since the previous one.
 */
export const generateAgentStoryMap = async (
    storyMapId,
    files = [],
    { onUploadProgress, onEvent } = {}
) => {
    try {
        const formData = new FormData()
        files.forEach((file) => formData.append('files', file, file.name))
        formData.append('stream', 'true')

        let parsedChars = 0
        let terminal = null
        const parseNewLines = (text) => {
            let newlineAt
            while ((newlineAt = text.indexOf('\n', parsedChars)) !== -1) {
                const line = text.slice(parsedChars, newlineAt).trim()
                parsedChars = newlineAt + 1
                if (!line) continue
                let event
                try {
                    event = JSON.parse(line)
                } catch {
                    continue
                }
                if (event?.kind === 'result' || event?.kind === 'error') {
                    terminal = event
                }
                try {
                    onEvent?.(event)
                } catch {
                    // A listener bug must not kill the stream parse.
                }
            }
        }

        const response = await axiosNew({
            url: `support/storyMap/agent/generate/${storyMapId}`,
            method: 'post',
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: GENERATE_TIMEOUT_MS,
            responseType: 'text',
            onUploadProgress: (event) => {
                if (!onUploadProgress) return
                const total = event.total ?? 0
                onUploadProgress(total > 0 ? event.loaded / total : 0)
            },
            onDownloadProgress: (event) => {
                const text = event?.target?.responseText
                if (typeof text === 'string') parseNewLines(text)
            },
        })

        // Backstop: parse anything the last progress event didn't cover.
        const full = typeof response?.data === 'string' ? response.data : ''
        if (full) parseNewLines(full.endsWith('\n') ? full : `${full}\n`)

        if (
            terminal?.kind === 'result' &&
            terminal?.data?.story_map?.board
        ) {
            return {
                success: true,
                result: terminal.data,
                message: formatStoryMapMessage(
                    terminal.message,
                    'Story map draft generated'
                ),
            }
        }
        return {
            success: false,
            result: null,
            message: formatStoryMapMessage(
                terminal?.message,
                'Story map generation failed'
            ),
        }
    } catch (error) {
        return {
            success: false,
            result: null,
            message: formatStoryMapMessage(
                error?.response?.data?.message,
                formatStoryMapMessage(error?.message, 'Story map generation failed')
            ),
        }
    }
}
